import React from 'react';
import { ErrorMessage, Field } from 'formik';
import {
  FormControl, Text,
} from 'native-base';
import { DatePickerInput } from 'react-native-paper-dates';
import SelectBox from 'react-native-multi-selectbox';
import { xorBy } from 'lodash';
import { Image, Platform } from 'react-native';
import { ActivityIndicator, IconButton, TextInput } from 'react-native-paper';
import * as ImagePicker from 'expo-image-picker';
import { addDays } from 'date-fns';
import { getDocumentAsync } from 'expo-document-picker';
import { useMutation } from '@apollo/client';
import { styles } from '../../screens/Common.style';
import AutocompleteLocation from '../../components/commons/AutocompleteLocation';
import { AGE_CATEGORY, EVENT_LEVEL, EVENT_TYPE } from '../../utils/constant';
import { ConvertDocumentToImage } from '../../gql/event/mutation';

function FormStepOne() {
  return (
    <>
      <FormControl style={styles.field}>
        <FormControl.Label>Nom de l'évènement</FormControl.Label>
        <Field id="name" name="name">
          {({ field, form }) => (
            <TextInput
              style={styles.whiteBackground}
              value={field.value}
              onChangeText={(text) => form.setFieldValue('name', text)}
              mode="outlined"
              label="Nom de l'évènement"
            />
          )}
        </Field>
        <ErrorMessage name="name" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
      <FormControl style={styles.field}>
        <FormControl.Label>Type d'évènement</FormControl.Label>
        <Field id="type" name="type">
          {({ field, form }) => (
            <SelectBox
              label=""
              options={Object.entries(EVENT_TYPE).map((e) => ({ id: e[0], item: e[1] }))}
              value={field.value}
              containerStyle={styles.whiteBackground}
              onChange={(value) => form.setFieldValue('type', value)}
            />
          )}
        </Field>
        <ErrorMessage name="type" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
      <FormControl style={styles.field}>
        <FormControl.Label>Lieu</FormControl.Label>
        <Field
          id="location"
          name="location"
        >
          {({ field, form }) => (
            <AutocompleteLocation
              style={styles.highZIndex}
              value={field.value}
              handleChangeText={(value) => form.setFieldValue('location', value)}
              handleSubmit={(value) => form.setFieldValue('location', value)}
            />
          )}
        </Field>
        <ErrorMessage name="location" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
      <FormControl style={styles.field}>
        <FormControl.Label>Date de début</FormControl.Label>
        <Field
          id="startAt"
          name="startAt"
        >
          {({ field, form }) => (
            <DatePickerInput
              presentationStyle="pageSheet"
              style={styles.whiteBackground}
              locale="fr-FR"
              label="Date de début"
              value={new Date(field.value ?? Date.now())}
              onChange={(d) => {
                d.setHours(d.getHours() + 12);
                if (d !== new Date(field.value)) form.setFieldValue('startAt', d);
              }}
              inputMode="start"
              mode="flat"
            />
          )}
        </Field>
        <ErrorMessage name="startAt" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
      <FormControl style={styles.field}>
        <FormControl.Label>Date de fin</FormControl.Label>
        <Field
          id="endAt"
          name="endAt"
        >
          {({ field, form }) => (
            <DatePickerInput
              presentationStyle="pageSheet"
              style={styles.whiteBackground}
              locale="fr-FR"
              label="Date de fin"
              value={new Date(field.value ?? Date.now())}
              onChange={(d) => {
                d.setHours(d.getHours() + 12);
                if (d !== new Date(field.value)) form.setFieldValue('endAt', d);
              }}
              inputMode="start"
              mode="flat"
            />
          )}
        </Field>
        <ErrorMessage name="endAt" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
    </>
  );
}

function FormStepTwo({ values }) {
  const [convertDocumentToImage, { loading: isConvertingMedia }] = useMutation(
    ConvertDocumentToImage,
  );

  function onMultiChange({ field, form, item }) {
    form.setFieldValue('categories', xorBy(field.value, [item], 'id'));
  }

  async function readDocumentAsBase64(result) {
    if (Platform.OS === 'web') return result.assets[0].uri;
    const asset = result.assets[0];
    const FileSystem = await import('expo-file-system');
    const base64 = await FileSystem.readAsStringAsync(asset.uri, {
      encoding: FileSystem.EncodingType.Base64,
    });
    return `data:${asset.mimeType};base64,${base64}`;
  }

  async function handlePickMediaPdf(form) {
    const result = await getDocumentAsync({ type: ['application/pdf'] });
    if (result.canceled) return;
    const base64File = await readDocumentAsBase64(result);
    const { data } = await convertDocumentToImage({ variables: { base64: base64File } });
    form.setFieldValue('media', data.convertDocumentToImage);
  }

  return (
    <>
      <FormControl style={styles.field}>
        <FormControl.Label>Type d'évènement</FormControl.Label>
        <Field id="level" name="level">
          {({ field, form }) => (
            <SelectBox
              label=""
              options={Object.entries(EVENT_LEVEL).map((e) => ({ id: e[0], item: e[1] }))}
              value={field.value}
              containerStyle={styles.whiteBackground}
              onChange={(value) => {
                form.setFieldValue('level', value);
                form.setFieldValue('registerEndAt', addDays(new Date(values.startAt), value.item === EVENT_LEVEL.REGIONAL ? -15 : -30));
              }}
            />
          )}
        </Field>
        <ErrorMessage name="level" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
      <FormControl style={styles.field}>
        <FormControl.Label>Date de début d'inscription</FormControl.Label>
        <Field
          id="registerStartAt"
          name="registerStartAt"
        >
          {({ field, form }) => (
            <DatePickerInput
              presentationStyle="pageSheet"
              style={styles.whiteBackground}
              locale="fr-FR"
              label="Date de début d'inscription"
              value={new Date(field.value ?? Date.now())}
              onChange={(d) => {
                d.setHours(d.getHours() + 12);
                if (d !== new Date(field.value)) form.setFieldValue('registerStartAt', d);
              }}
              inputMode="start"
              mode="flat"
            />
          )}
        </Field>
        <ErrorMessage name="registerStartAt" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
      <FormControl style={styles.field}>
        <FormControl.Label>Date limite d'inscription</FormControl.Label>
        <Field
          id="registerEndAt"
          name="registerEndAt"
        >
          {({ field, form }) => (
            <DatePickerInput
              presentationStyle="pageSheet"
              style={styles.whiteBackground}
              locale="fr-FR"
              label="Date limite d'inscription"
              value={new Date(field.value ?? Date.now())}
              onChange={(d) => {
                d.setHours(d.getHours() + 12);
                if (d !== new Date(field.value)) form.setFieldValue('registerEndAt', d);
              }}
              inputMode="start"
              mode="flat"
            />
          )}
        </Field>
        <ErrorMessage name="registerEndAt" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
      <FormControl style={styles.field}>
        <FormControl.Label>Catégories</FormControl.Label>
        <Field
          id="categories"
          name="categories"
        >
          {({ field, form }) => (
            <SelectBox
              label=""
              containerStyle={styles.whiteBackground}
              options={Object.entries(AGE_CATEGORY).map((e) => ({ id: e[0], item: e[1].name }))}
              selectedValues={field.value}
              onMultiSelect={(item) => onMultiChange({ field, form, item })}
              onTapClose={(item) => onMultiChange({ field, form, item })}
              isMulti
            />
          )}
        </Field>
        <ErrorMessage name="categories" render={(message) => (<Text style={styles.error}>{message}</Text>)} />
      </FormControl>
      <FormControl style={styles.field}>
        <FormControl.Label>Media</FormControl.Label>
        <Field
          id="media"
          name="media"
        >
          {({ field, form }) => (
            <>
              <IconButton
                icon="image-edit"
                size={20}
                disabled={isConvertingMedia}
                onPress={async () => {
                  const result = await ImagePicker.launchImageLibraryAsync({
                    mediaTypes: ImagePicker.MediaTypeOptions.Images,
                    allowsEditing: true,
                    aspect: [4, 3],
                    quality: 1,
                    base64: true,
                  });
                  if (!result.canceled) {
                    const media = Platform.OS === 'web' ? result.assets[0].uri : `data:${result.assets[0].mimeType};base64,${result.assets[0].base64}`;
                    form.setFieldValue('media', media);
                  }
                }}
              />
              <IconButton
                icon="file-pdf-box"
                size={20}
                disabled={isConvertingMedia}
                onPress={() => handlePickMediaPdf(form)}
              />
              {isConvertingMedia && <ActivityIndicator animating size={20} />}
              {!isConvertingMedia && field.value
                && (
                  <Image
                    style={{
                      alignItems: 'center',
                      backgroundColor: 'transparent',
                      width: 460,
                      height: 200,
                      resizeMode: 'cover',
                    }}
                    source={{ uri: field.value }}
                  />
                )}
            </>
          )}
        </Field>
      </FormControl>

      <FormControl style={styles.field}>
        <FormControl.Label>Information supplémentaire</FormControl.Label>
        <Field
          id="infoMedia"
          name="infoMedia"
        >
          {({ form }) => (
            <IconButton
              icon="file-edit"
              size={20}
              onPress={async () => {
                const result = await getDocumentAsync({
                  type: ['application/pdf', 'image/jpeg'],
                });
                if (result.type === 'success') {
                  let base64File = result.uri;
                  if (Platform.OS !== 'web') {
                    const FileSystem = React.lazy(() => import('expo-file-system'));
                    // For native platforms, asset.uri is a file path
                    base64File = await FileSystem.readAsStringAsync(result.uri, {
                      encoding: FileSystem.EncodingType.Base64,
                    });
                    // Add the data URL prefix using asset.mimeType
                    base64File = `data:${result.mimeType};base64,${base64File}`;
                  }
                  form.setFieldValue('infoMedia', base64File);
                }
              }}
            />
          )}
        </Field>
      </FormControl>
    </>
  );
}

export { FormStepOne, FormStepTwo };
